import { useAuth0 } from '@auth0/auth0-react';
import {
  InformationCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
  resolveBookingIntentSource,
  retainBookingIntentSource,
} from 'hotel/bookingIntentLineage';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'hotel/navigation';
import { useHotelScope } from 'hotel/scope';
import { useEffect, useRef } from 'react';

import { AddOrRemoveFromShortListButton } from 'components/AddOrRemoveFromShortListButton';
import { SecondaryButton } from 'components/Button';
import CollapsibleSection from 'components/CollapsibleSection';
import ImageGallery from 'components/ImageGallery';
import Loading from 'components/Loading';
import { HotelSearchQuery, useHotelSearchSingleQuery } from 'generated/graphql';
import { useAppSelector } from 'store/store';
import { calculateDistance } from 'utilities/calculateDistance';
import getEnumLabel from 'utilities/getEnumLabel';
import { calculateGuestsPerRoom } from 'utilities/guestQuantityHelpers';
import { formatCents, PLUS_TAX_TEXT } from 'utilities/money';
import { formatDateTime, formatTime } from 'utilities/parsers';

// Keywords to identify smoking-related information in room descriptions
const SMOKING_KEYWORDS = [
  // Full words and common phrases
  'smoking',
  'non-smoking',
  'nonsmoking',
  'non smoking',
  'smoke-free',
  'smoke free',
  'smokefree',
  'no smoking',
  'nosmoking',
  'smoke',
  'tobacco',
  'cigarette',
  'cigar',
  // Common abbreviations and variations
  'nonsmok',
  'non-smok',
  'nosmok',
  'no-smok',
  'smok',
  'smk',
  // Hotel industry abbreviations
  'nsr', // Non-Smoking Room
  'sr', // Smoking Room
];

// Short abbreviations that require word boundary matching to avoid false positives
const SMOKING_KEYWORDS_SHORT = ['ns', 'sm'];

interface LocationState {
  bookingSourceKey?: string | null;
  hotel: HotelSearchQuery['hotelSearch']['results'][number]['hotel'];
  offers: HotelSearchQuery['hotelSearch']['results'][number]['offers'];
  checkInDate: string;
  checkOutDate: string;
  roomQuantity: string;
  guestQuantity: string;
  latitude: string;
  longitude: string;
  description: string;
  accessibleRoomsOnly?: string;
}

const HotelDetail = () => {
  const { amenities } = useAppSelector((state) => state.enums);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth0();
  const hotelScope = useHotelScope();
  const navigationToken = JSON.stringify([
    location.key,
    user?.sub,
    hotelScope?.clientId,
    hotelScope?.stateKey,
  ]);
  const currentNavigation = useRef<string | null>(navigationToken);
  currentNavigation.current = navigationToken;
  useEffect(() => {
    currentNavigation.current = navigationToken;
    return () => {
      currentNavigation.current = null;
    };
  }, [navigationToken]);
  const { hotelId } = useParams<{ hotelId: string }>();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState | null;

  const checkInDate =
    state?.checkInDate || searchParams.get('checkInDate') || '';
  const checkOutDate =
    state?.checkOutDate || searchParams.get('checkOutDate') || '';
  const roomQuantity = Number(
    state?.roomQuantity || searchParams.get('roomQuantity') || 1,
  );
  const guestQuantity = Number(
    state?.guestQuantity || searchParams.get('guestQuantity') || 1,
  );
  const cityLatitude = parseFloat(
    state?.latitude ?? (searchParams.get('latitude') || '0'),
  );
  const cityLongitude = parseFloat(
    state?.longitude ?? (searchParams.get('longitude') || '0'),
  );
  const cityName =
    state?.description ?? (searchParams.get('description') || '');
  const accessibleRoomsOnly =
    state?.accessibleRoomsOnly === 'true' ||
    searchParams.get('accessibleRoomsOnly') === 'true';

  const amadeusHotelId = hotelId || state?.hotel?.amadeusHotelId;

  const bookingType = useAppSelector((state) => state.booking.bookingType);

  // Convert total guests to guests per room for API call
  const guestsPerRoom = calculateGuestsPerRoom(guestQuantity, roomQuantity);

  // Create API query variables with guests per room
  const apiQueryVariables = {
    amadeusHotelId: amadeusHotelId || '',
    checkInDate,
    checkOutDate,
    guestQuantity: guestsPerRoom, // Use guests per room for API
    roomQuantity,
    accessibleRoomsOnly,
  };

  // For group bookings, override with standard values
  if (bookingType === 'group') {
    apiQueryVariables.guestQuantity = 1;
    apiQueryVariables.roomQuantity = 1;
  }

  const { data: hotelData, loading } = useHotelSearchSingleQuery({
    variables: apiQueryVariables,
    skip: !amadeusHotelId,
    fetchPolicy: 'cache-and-network',
  });

  const handleBackToResults = () => {
    navigate(-1);
  };

  const handleBooking = async (
    offer: HotelSearchQuery['hotelSearch']['results'][number]['offers'][number],
  ) => {
    let bookingSourceKey: string | null = null;
    try {
      const scope = {
        actorId: user?.sub ?? '',
        clientId: hotelScope?.clientId ?? '',
      };
      const sourceKey = resolveBookingIntentSource(
        scope,
        location.key,
        state?.bookingSourceKey,
      );
      await retainBookingIntentSource(scope, location.key, sourceKey);
      bookingSourceKey = sourceKey;
    } catch {
      // Browsing remains available; checkout must not start a fresh lineage.
    }
    if (currentNavigation.current !== navigationToken) return;
    const bookingDetails = {
      amadeusHotelId: amadeusHotelId || '',
      checkInDate,
      checkOutDate,
      roomQuantity,
      guestQuantity, // This represents total guests for UI display
      offer, // The offer object contains guestsPerRoom from the API
      hotel: hotelData?.hotelSearchSingle?.hotel,
    };
    navigate('/selfbooking', {
      state: {
        bookingSourceKey,
        bookingDetails,
      },
    });
  };

  const hotel = hotelData?.hotelSearchSingle?.hotel || state?.hotel;
  const offers = hotelData?.hotelSearchSingle?.offers || state?.offers;

  if (loading) {
    return <Loading />;
  }

  if (!hotel || !offers) {
    return (
      <div className="flex size-full items-center justify-center">
        <p>
          Hotel information not available.{' '}
          <button
            onClick={handleBackToResults}
            className="text-secondaryBlue underline"
          >
            Return to search
          </button>
        </p>
      </div>
    );
  }

  const formattedAddress =
    hotel.formattedAddress ||
    `${hotel.address?.addressLine1 || ''}, ${hotel.address?.city || ''} ${hotel.address?.state || ''} ${hotel.address?.zip || ''}`;

  let distance = null;
  if (cityLatitude && cityLongitude && hotel.latitude && hotel.longitude) {
    distance = calculateDistance(
      cityLatitude,
      cityLongitude,
      hotel.latitude,
      hotel.longitude,
    );
  }

  const starRating = hotel.starRating || 0;
  const adaAccessibilityFeatures = hotel.adaAccessibilityFeatures ?? [];

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* Back Button */}
      {/* Container with max-width for hotel details */}
      <div className="bg-white">
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
          <div className="pb-2 pt-6">
            <button
              onClick={handleBackToResults}
              className="flex items-center text-gray-600 transition-colors duration-200 hover:text-gray-900"
            >
              <ArrowLeftIcon className="mr-2 size-5" />
              <span className="font-medium">Back to results</span>
            </button>
          </div>

          {/* Hotel Name and Address - moved above gallery */}
          <div className="pb-3 pt-4">
            <div className="flex items-center justify-between">
              <h1 className="font-oswald text-3xl font-bold text-black">
                {hotel.name}
              </h1>
              {bookingType === 'group' && hotel && (
                <div className="hidden lg:block">
                  <AddOrRemoveFromShortListButton hotel={hotel} />
                </div>
              )}
            </div>
            <div className="my-4 text-base text-primaryGray">
              {distance !== null && (
                <span className="mr-2">
                  {distance} miles from {cityName}
                </span>
              )}
              <span className="border-l-2 border-gray-300 px-2  text-primaryGray">
                {formattedAddress}
              </span>
            </div>

            <div className="mb-4 flex items-center">
              <div className="flex items-center rounded-xl border border-gray-200 bg-white px-4 py-1">
                <StarIconSolid className="mr-1 size-5 text-accentGreen" />
                <span className="font-bold text-black">
                  {starRating === 0 ? '-' : starRating}
                </span>
              </div>
              <div className="ml-4 flex flex-wrap gap-2">
                {hotel.amenities && hotel.amenities.length > 0 && (
                  <>
                    {hotel.amenities.slice(0, 2).map((amenity, index) => (
                      <span
                        key={index}
                        className="flex items-center rounded-lg bg-lightBlue px-4 text-base text-accentBlue"
                      >
                        {getEnumLabel(amenities, amenity) || amenity}
                      </span>
                    ))}
                    {hotel.amenities.length > 2 && (
                      <span className="flex items-center rounded-lg bg-lightBlue px-4 text-base text-accentBlue">
                        +{hotel.amenities.length - 2} more
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Mobile Add to Shortlist Button - positioned below rating */}
            {bookingType === 'group' && hotel && (
              <div className="mb-4 lg:hidden">
                <AddOrRemoveFromShortListButton hotel={hotel} />
              </div>
            )}
          </div>

          <div className="py-3">
            <ImageGallery images={hotel.images || []} altText={hotel.name} />
          </div>

          <div className="pb-24 pt-0">
            <CollapsibleSection
              title="View Hotel Information"
              icon={
                <InformationCircleIcon className="size-6 text-accentGreen" />
              }
              initialExpanded
              headerClassName="flex w-full items-center justify-between py-4"
            >
              <div className="space-y-6">
                <div className="border-b pb-4">
                  <div className="space-y-3 md:grid md:grid-cols-4 md:items-center md:space-y-0">
                    <h3 className="font-arimo text-base text-gray-500">
                      Amenities
                    </h3>
                    <div className="flex flex-wrap gap-2 md:col-span-3">
                      {hotel.amenities &&
                        hotel.amenities.map((amenity, index) => (
                          <span
                            key={index}
                            className="flex items-center rounded-lg bg-lightBlue px-4 text-base text-accentBlue"
                          >
                            {getEnumLabel(amenities, amenity) || amenity}
                          </span>
                        ))}
                    </div>
                  </div>
                </div>

                {adaAccessibilityFeatures.length > 0 && (
                  <div className="border-b pb-4">
                    <div className="space-y-3 md:grid md:grid-cols-4 md:items-center md:space-y-0">
                      <h3 className="font-arimo text-base text-gray-500">
                        ADA Accommodation
                      </h3>
                      <div className="flex flex-wrap gap-2 md:col-span-3">
                        {adaAccessibilityFeatures.map((feature) => (
                          <span
                            key={feature}
                            className="flex items-center rounded-lg bg-lightBlue px-4 text-base text-accentBlue"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-b pb-4">
                  <div className="space-y-3 md:grid md:grid-cols-4 md:items-center md:space-y-0">
                    <h3 className="font-arimo text-base text-gray-500">
                      Check-in time
                    </h3>
                    <p className="text-base text-gray-900 md:col-span-3">
                      {formatTime(hotel.checkInTime ?? '')}
                    </p>
                  </div>
                </div>

                <div className="border-b pb-4">
                  <div className="space-y-3 md:grid md:grid-cols-4 md:items-center md:space-y-0">
                    <h3 className="font-arimo text-base text-gray-500">
                      Check-out time
                    </h3>
                    <p className="text-base text-gray-900 md:col-span-3">
                      {formatTime(hotel.checkOutTime ?? '')}
                    </p>
                  </div>
                </div>

                <div>
                  <div className="space-y-3 md:grid md:grid-cols-4 md:items-center md:space-y-0">
                    <h3 className="font-arimo text-base text-gray-500">
                      Description
                    </h3>
                    <p className="text-base leading-relaxed text-gray-900 md:col-span-3">
                      {hotel.description?.trim() ||
                        'No description was provided by the hotel.'}
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          </div>
        </div>
      </div>
      {bookingType === 'instant' && (
        <div className="border-t bg-gray-50">
          <div className="mx-auto max-w-6xl px-6 pb-24 pt-10 sm:px-8 lg:px-12">
            <div className="flex items-center pb-4">
              <p className="mr-2 text-lg text-gray-500">Select a room </p>
              <p className="text-lg text-secondaryBlue">
                ({offers?.length} options available)
              </p>
            </div>

            {offers ? (
              offers.map((offer, index) => (
                <div
                  key={index}
                  className={`${index !== offers.length - 1 ? 'mb-4' : ''} mt-4 rounded-md border bg-white p-4 sm:p-6`}
                >
                  {/* Mobile: Stack vertically, Desktop: Side by side */}
                  <div className="space-y-4 sm:flex sm:items-start sm:justify-between sm:space-y-0">
                    <div className="flex-1">
                      <h3 className="font-oswald text-lg font-medium sm:text-xl">
                        {offer.description?.split('\n')[0] ||
                          `${offer.roomBeds}x ${offer.roomBedType} Bed Room`}
                      </h3>
                      {(() => {
                        const descriptionLines =
                          offer.description?.split('\n') || [];
                        const additionalLines = descriptionLines
                          .slice(1)
                          .filter((line) => line.trim());

                        if (additionalLines.length === 0) return null;

                        return (
                          <div className="mt-2 space-y-1">
                            {additionalLines.map((line, idx) => {
                              const trimmedLine = line.trim();
                              const lowerLine = trimmedLine.toLowerCase();
                              const isSmokingInfo =
                                SMOKING_KEYWORDS.some((keyword) =>
                                  lowerLine.includes(keyword),
                                ) ||
                                SMOKING_KEYWORDS_SHORT.some((keyword) => {
                                  const regex = new RegExp(
                                    `\\b${keyword}\\b`,
                                    'i',
                                  );
                                  return regex.test(trimmedLine);
                                });

                              return (
                                <p
                                  key={idx}
                                  className={`text-sm ${isSmokingInfo ? 'font-medium text-gray-900' : 'text-gray-600'}`}
                                >
                                  {isSmokingInfo && (
                                    <span className="sr-only">
                                      Smoking policy:{' '}
                                    </span>
                                  )}
                                  {trimmedLine}
                                </p>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Pricing section - full width on mobile, right-aligned on desktop */}
                    <div className="sm:ml-6 sm:shrink-0 sm:text-right">
                      <div className="flex items-baseline justify-between sm:justify-end sm:space-x-1">
                        <span className="text-sm text-gray-500 sm:hidden">
                          Price:
                        </span>
                        <div className="text-right">
                          <p className="font-oswald text-lg font-medium sm:text-xl">
                            <span className="text-[#72AD17]">
                              {formatCents(offer.avgPrice)}
                            </span>
                            <span className="text-primaryGray">
                              {' '}
                              / night {PLUS_TAX_TEXT}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:block sm:text-right">
                        <span className="text-sm text-gray-500 sm:hidden">
                          Total:
                        </span>
                        <p className="text-sm text-gray-500 sm:mt-1">
                          {formatCents(offer.avgPrice * (offer.nights || 1))}{' '}
                          {PLUS_TAX_TEXT} ({offer.nights || 1}{' '}
                          {(offer.nights || 1) === 1 ? 'night' : 'nights'})
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Cancellation and booking section */}
                  {offer.cancellationDetails && (
                    <div className="mt-6 space-y-4 sm:flex sm:items-center sm:justify-between sm:space-y-0">
                      <p className="text-sm font-bold sm:text-base">
                        {offer.cancellationDetails.nonRefundable
                          ? 'Non-refundable'
                          : `Cancel until ${formatDateTime(offer.cancellationDetails.deadline ?? '')}`}
                      </p>
                      <SecondaryButton
                        className="w-full rounded-md bg-darkBlue text-white sm:w-auto"
                        onClick={() => {
                          handleBooking(offer);
                        }}
                      >
                        Book this room
                      </SecondaryButton>
                    </div>
                  )}

                  {!offer.cancellationDetails && (
                    <div className="mt-6">
                      <SecondaryButton
                        className="w-full rounded-md bg-darkBlue text-white sm:ml-auto sm:block sm:w-auto"
                        onClick={() => {
                          handleBooking(offer);
                        }}
                      >
                        Book this room
                      </SecondaryButton>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">
                No offers available for this hotel
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelDetail;
